/** Picking where a founder's computer runs: the plan's regions, a suggestion from the browser's time zone. Pure. */
import type { CreateFounderInput, PlanRegion, Plans } from './schemas'

export type GeoKey = 'east' | 'central' | 'west'

// Pacific and Mountain zones west of Denver.
const WEST = new Set([
  'America/Los_Angeles', 'America/Vancouver', 'America/Tijuana', 'America/Phoenix', 'America/Boise', 'America/Edmonton',
  'America/Yellowknife', 'America/Inuvik', 'America/Cambridge_Bay', 'America/Whitehorse', 'America/Dawson',
  'America/Dawson_Creek', 'America/Fort_Nelson', 'America/Creston', 'America/Hermosillo', 'America/Mazatlan',
  'America/Ciudad_Juarez', 'America/Anchorage', 'America/Juneau', 'America/Sitka', 'America/Yakutat', 'America/Nome',
  'America/Metlakatla', 'America/Adak', 'Pacific/Honolulu',
  'US/Pacific', 'US/Arizona', 'US/Alaska', 'US/Hawaii', 'Canada/Pacific', 'Canada/Mountain', 'Canada/Yukon', 'PST8PDT',
])

// Central zones, and Denver.
const CENTRAL = new Set([
  'America/Chicago', 'America/Denver', 'America/Winnipeg', 'America/Regina', 'America/Swift_Current',
  'America/Rankin_Inlet', 'America/Resolute', 'America/Rainy_River', 'America/Menominee', 'America/Indiana/Knox',
  'America/Indiana/Tell_City', 'America/North_Dakota/Center', 'America/North_Dakota/New_Salem',
  'America/North_Dakota/Beulah', 'US/Central', 'US/Mountain', 'US/Indiana-Starke', 'Canada/Central',
  'Canada/Saskatchewan', 'CST6CDT', 'MST7MDT',
])

// Eastern zones, and Atlantic Canada, whose nearest data centres are New York's.
const EAST = new Set([
  'America/New_York', 'America/Toronto', 'America/Montreal', 'America/Detroit', 'America/Nipigon', 'America/Thunder_Bay',
  'America/Iqaluit', 'America/Pangnirtung', 'America/Nassau', 'America/Indianapolis', 'America/Fort_Wayne',
  'America/Louisville', 'America/Indiana/Indianapolis', 'America/Indiana/Marengo', 'America/Indiana/Petersburg',
  'America/Indiana/Vevay', 'America/Indiana/Vincennes', 'America/Indiana/Winamac', 'America/Kentucky/Louisville',
  'America/Kentucky/Monticello', 'America/Halifax', 'America/Moncton', 'America/Glace_Bay', 'America/Goose_Bay',
  'America/St_Johns', 'US/Eastern', 'US/Michigan', 'US/East-Indiana', 'Canada/Eastern', 'Canada/Atlantic',
  'Canada/Newfoundland', 'EST5EDT',
])

/** The region closest to a time zone, or null when the zone says nothing useful (UTC, Europe, unknown). */
export function geoForTimeZone(timeZone: string | undefined | null): GeoKey | null {
  if (!timeZone) return null
  if (WEST.has(timeZone)) return 'west'
  if (CENTRAL.has(timeZone)) return 'central'
  if (EAST.has(timeZone)) return 'east'
  return null
}

export function browserTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return undefined
  }
}

export interface RegionPick {
  /** The region to preselect; null when none is available. */
  value: string | null
  /** The region that is closest by time zone and available, for the "Suggested" mark. */
  suggested: string | null
}

/**
 * The time zone's region when it's available, else the plan's default, else the first available one. The
 * "Suggested" mark goes only on a time-zone match that can be picked.
 */
export function pickRegion(plans: Pick<Plans, 'regions' | 'defaultRegion'>, timeZone: string | undefined | null): RegionPick {
  const ok = (key: string | null) => plans.regions.some((r) => r.key === key && r.available)
  const near = geoForTimeZone(timeZone)
  if (near && ok(near)) return { value: near, suggested: near }
  const wanted = near ?? plans.defaultRegion
  if (ok(wanted)) return { value: wanted, suggested: null }
  return { value: plans.regions.find((r) => r.available)?.key ?? null, suggested: null }
}

/** The vendor region and size the API resolved for a picked region, or null when it can't be picked. */
export function resolvedBox(plans: Pick<Plans, 'regions'>, key: string | null): { region: string; boxSize: string; priceMonthly: number } | null {
  const r: PlanRegion | undefined = plans.regions.find((x) => x.key === key)
  if (!r?.available || !r.region || !r.size || r.priceMonthly === null) return null
  return { region: r.region, boxSize: r.size, priceMonthly: r.priceMonthly }
}

/** The New founder body: the picked region's resolved vendor region and size go as `region` and `boxSize`. */
export function newFounderBody(
  input: { name: string; slug: string; subdomain: string },
  plans: Pick<Plans, 'regions'>,
  regionKey: string | null,
): CreateFounderInput | null {
  const box = resolvedBox(plans, regionKey)
  if (!box) return null
  return { name: input.name.trim(), slug: input.slug, region: box.region, boxSize: box.boxSize, ...(input.subdomain ? { subdomain: input.subdomain } : {}) }
}
